import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdWarning, MdSchedule, MdEmail, MdCheckCircle, MdRefresh, MdMessage, MdPhone } from 'react-icons/md';
import { recibosAPI } from '../services/api';

const AlertsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const FilterLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}20;
  }
`;

const NumberInput = styled.input`
  width: 80px;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-align: center;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}20;
  }
`;

const TabButton = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }
`;

const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const AlertCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.type === 'overdue' ? props.theme.colors.danger + '20' : props.theme.colors.warning + '20'};
  color: ${props => props.type === 'overdue' ? props.theme.colors.danger : props.theme.colors.warning};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1.5rem;
`;

const AlertBadge = styled.div`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background-color: ${props => props.type === 'overdue' ? props.theme.colors.danger : props.theme.colors.warning};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const AlertStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const StatusText = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ClientName = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const AlertDetails = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const DetailLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const DetailValue = styled.span`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const Amount = styled.div`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.bold};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ActionButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const NotificationButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const NotificationButton = styled.button`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => {
    if (props.variant === 'message') return props.theme.colors.success || '#4CAF50';
    if (props.variant === 'call') return props.theme.colors.info || '#2196F3';
    return props.theme.colors.backgroundLight;
  }};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Alerts = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Todas');
  const [filterNumber, setFilterNumber] = useState(7);
  const [filterUnit, setFilterUnit] = useState('días');
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingNotification, setSendingNotification] = useState({});

  // Cargar recibos y generar alertas dinámicamente
  useEffect(() => {
    fetchRecibos();
  }, []);

  const fetchRecibos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await recibosAPI.getAll();
      const todosRecibos = response.data.data || [];
      
      // Filtrar solo recibos con saldo pendiente
      const recibosConSaldo = todosRecibos.filter(recibo => 
        parseFloat(recibo.saldo_pendiente || 0) > 0 &&
        recibo.estado !== 'pagado' &&
        recibo.estado !== 'cancelado'
      );
      
      setRecibos(recibosConSaldo);
    } catch (err) {
      console.error('Error al cargar recibos:', err);
      setError(err.message || 'Error al cargar recibos');
    } finally {
      setLoading(false);
    }
  };

  // Calcular días hasta vencimiento
  const calcularDiasHastaVencimiento = (fechaVencimiento) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fecha = new Date(fechaVencimiento);
    fecha.setHours(0, 0, 0, 0);
    const diffTime = fecha - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Generar alertas desde recibos
  const alertasGeneradas = recibos.map(recibo => ({
    id: recibo.id,
    recibo_id: recibo.id,
    numero_recibo: recibo.numero_recibo,
    cliente_nombre: recibo.cliente_nombre,
    cliente_telefono: recibo.cliente_telefono,
    cliente_email: recibo.cliente_email,
    monto_total: recibo.monto_total,
    saldo_pendiente: recibo.saldo_pendiente,
    fecha_vencimiento: recibo.fecha_vencimiento,
    recibo_estado: recibo.estado,
    dias_hasta_vencimiento: calcularDiasHastaVencimiento(recibo.fecha_vencimiento),
    tipo: calcularDiasHastaVencimiento(recibo.fecha_vencimiento) < 0 ? 'vencido' : 'proximo'
  }));

  // Funciones para enviar notificaciones
  const handleSendMessage = async (alert) => {
    setSendingNotification(prev => ({ ...prev, [`msg_${alert.id}`]: true }));
    
    try {
      // Simular envío de mensaje (aquí integrarías con API de SMS/WhatsApp)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mensaje = `Hola ${alert.cliente_nombre}, le recordamos que tiene un recibo pendiente #${alert.numero_recibo} por $${alert.saldo_pendiente.toLocaleString('es-MX')}. Fecha de vencimiento: ${new Date(alert.fecha_vencimiento).toLocaleDateString('es-MX')}.`;
      
      console.log('Enviando mensaje a:', alert.cliente_telefono);
      console.log('Contenido:', mensaje);
      
      window.alert('✅ Mensaje enviado correctamente a ' + alert.cliente_nombre);
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      window.alert('❌ Error al enviar el mensaje. Por favor intente nuevamente.');
    } finally {
      setSendingNotification(prev => ({ ...prev, [`msg_${alert.id}`]: false }));
    }
  };

  const handleMakeCall = async (alert) => {
    setSendingNotification(prev => ({ ...prev, [`call_${alert.id}`]: true }));
    
    try {
      // Simular llamada (aquí integrarías con API de telefonía)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const telefono = alert.cliente_telefono || 'No disponible';
      
      if (telefono === 'No disponible') {
        window.alert('⚠️ El cliente no tiene teléfono registrado');
        return;
      }
      
      console.log('Iniciando llamada a:', telefono);
      console.log('Cliente:', alert.cliente_nombre);
      console.log('Recibo:', alert.numero_recibo);
      
      // En producción, aquí se abriría la aplicación de llamadas o se usaría una API
      if (window.confirm(`¿Desea llamar a ${alert.cliente_nombre} al número ${telefono}?`)) {
        window.location.href = `tel:${telefono}`;
      }
    } catch (error) {
      console.error('Error al iniciar llamada:', error);
      window.alert('❌ Error al iniciar la llamada. Por favor intente nuevamente.');
    } finally {
      setSendingNotification(prev => ({ ...prev, [`call_${alert.id}`]: false }));
    }
  };

  // Convertir el filtro a días
  const getDaysFromFilter = () => {
    const number = parseInt(filterNumber) || 0;
    switch (filterUnit) {
      case 'días':
        return number;
      case 'meses':
        return number * 30;
      case 'años':
        return number * 365;
      default:
        return number;
    }
  };

  // Calcular estadísticas
  const estadisticas = {
    total: alertasGeneradas.length,
    vencidos: alertasGeneradas.filter(a => a.tipo === 'vencido').length,
    proximos: alertasGeneradas.filter(a => a.tipo === 'proximo').length
  };

  // Filtrar alertas según tab y rango de días
  const filteredAlerts = alertasGeneradas.filter(alert => {
    const diasVencimiento = alert.dias_hasta_vencimiento;
    const esVencido = diasVencimiento < 0;
    
    if (activeTab === 'Todas') return true;
    
    const days = getDaysFromFilter();
    
    if (activeTab === 'Vencidos') {
      return esVencido && Math.abs(diasVencimiento) <= days;
    }
    
    if (activeTab === 'Proximos') {
      return !esVencido && diasVencimiento >= 0 && diasVencimiento <= days;
    }
    
    return true;
  });

  // Ordenar alertas: primero vencidos (más antiguos primero), luego próximos (más cercanos primero)
  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    if (a.tipo === 'vencido' && b.tipo === 'vencido') {
      return a.dias_hasta_vencimiento - b.dias_hasta_vencimiento; // Más vencidos primero
    }
    if (a.tipo === 'vencido') return -1;
    if (b.tipo === 'vencido') return 1;
    return a.dias_hasta_vencimiento - b.dias_hasta_vencimiento; // Más próximos primero
  });

  if (loading) {
    return (
      <AlertsContainer>
        <PageHeader>
          <Title>Alertas y Notificaciones</Title>
        </PageHeader>
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Cargando alertas...
        </div>
      </AlertsContainer>
    );
  }

  return (
    <AlertsContainer>
      <PageHeader>
        <Title>Alertas y Notificaciones</Title>
        <FilterSection>
          <TabButton active={activeTab === 'Todas'} onClick={() => setActiveTab('Todas')}>
            Todas ({estadisticas.total})
          </TabButton>
          <TabButton active={activeTab === 'Vencidos'} onClick={() => setActiveTab('Vencidos')}>
            Vencidos ({estadisticas.vencidos})
          </TabButton>
          <TabButton active={activeTab === 'Proximos'} onClick={() => setActiveTab('Proximos')}>
            Próximos ({estadisticas.proximos})
          </TabButton>

          {(activeTab === 'Vencidos' || activeTab === 'Proximos') && (
            <FilterGroup>
              <FilterLabel>Mostrar hasta:</FilterLabel>
              <NumberInput
                type="number"
                min="1"
                max="999"
                value={filterNumber}
                onChange={(e) => setFilterNumber(e.target.value)}
              />
              <Select
                value={filterUnit}
                onChange={(e) => setFilterUnit(e.target.value)}
              >
                <option value="días">Días</option>
                <option value="meses">Meses</option>
                <option value="años">Años</option>
              </Select>
              <FilterLabel>{activeTab === 'Vencidos' ? 'vencido' : 'para vencimiento'}</FilterLabel>
            </FilterGroup>
          )}

          <TabButton onClick={fetchRecibos} disabled={loading}>
            <MdRefresh style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Actualizar
          </TabButton>
        </FilterSection>
      </PageHeader>

      {error && (
        <div style={{ padding: '20px', backgroundColor: '#fee', color: '#c33', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {sortedAlerts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          {activeTab === 'Todas' && '¡Excelente! No hay recibos pendientes con alertas'}
          {activeTab === 'Vencidos' && 'No hay recibos vencidos'}
          {activeTab === 'Proximos' && 'No hay recibos próximos a vencer'}
        </div>
      ) : (
        <AlertsGrid>
          {sortedAlerts.map(alert => {
            const diasVencimiento = alert.dias_hasta_vencimiento;
            const esVencido = diasVencimiento < 0;
            
            return (
            <AlertCard key={alert.id}>
              <AlertHeader>
                <AlertIcon type={esVencido ? 'overdue' : 'upcoming'}>
                  {esVencido ? <MdWarning /> : <MdSchedule />}
                </AlertIcon>
                <AlertBadge type={esVencido ? 'overdue' : 'upcoming'}>
                  {esVencido 
                    ? `${Math.abs(diasVencimiento)} Día${Math.abs(diasVencimiento) !== 1 ? 's' : ''} Vencido${Math.abs(diasVencimiento) !== 1 ? 's' : ''}`
                    : diasVencimiento === 0 
                      ? 'Vence HOY'
                      : `Vence en ${diasVencimiento} Día${diasVencimiento !== 1 ? 's' : ''}`
                  }
                </AlertBadge>
              </AlertHeader>

              <AlertStatus>
                <StatusText>{alert.recibo_estado || 'Pendiente'}</StatusText>
              </AlertStatus>

              <ClientName>{alert.cliente_nombre || 'Sin nombre'}</ClientName>

              <Amount>
                ${(alert.saldo_pendiente || 0).toLocaleString('es-MX', { 
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2 
                })}
              </Amount>

              <AlertDetails>
                <DetailRow>
                  <DetailLabel>Recibo:</DetailLabel>
                  <DetailValue>{alert.numero_recibo}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Fecha Límite:</DetailLabel>
                  <DetailValue>
                    {new Date(alert.fecha_vencimiento).toLocaleDateString('es-MX', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Total:</DetailLabel>
                  <DetailValue>
                    ${(alert.monto_total || 0).toLocaleString('es-MX', { 
                      minimumFractionDigits: 2 
                    })}
                  </DetailValue>
                </DetailRow>
              </AlertDetails>

              <NotificationButtons>
                <NotificationButton 
                  variant="message"
                  onClick={() => handleSendMessage(alert)}
                  disabled={sendingNotification[`msg_${alert.id}`]}
                  title="Enviar mensaje SMS/WhatsApp"
                >
                  <MdMessage />
                  {sendingNotification[`msg_${alert.id}`] ? 'Enviando...' : 'Mensaje'}
                </NotificationButton>
                <NotificationButton 
                  variant="call"
                  onClick={() => handleMakeCall(alert)}
                  disabled={sendingNotification[`call_${alert.id}`]}
                  title="Llamar al cliente"
                >
                  <MdPhone />
                  {sendingNotification[`call_${alert.id}`] ? 'Llamando...' : 'Llamar'}
                </NotificationButton>
              </NotificationButtons>

              <div style={{ display: 'flex', gap: '10px' }}>
                <ActionButton onClick={() => navigate(`/recibos/${alert.recibo_id}`)}>
                  <MdCheckCircle style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Ver Detalles
                </ActionButton>
                <ActionButton onClick={() => navigate('/registrar-pago', { state: { reciboId: alert.recibo_id } })}>
                  <MdEmail style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Pagar
                </ActionButton>
              </div>
            </AlertCard>
            );
          })}
        </AlertsGrid>
      )}
    </AlertsContainer>
  );
};

export default Alerts;
